import { useState } from "react";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api, formatError, telHref } from "@/lib/api";
import { Field, SectionHeading, SuccessCard, FormCard, SubmitButton, Kente, LogoBadge } from "./kit";

const EMPTY = { name: "", email: "", phone: "", message: "" };

export const ContactSection = ({ content }) => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contacts", f);
      setDone(true);
      toast.success("Message sent");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-neutral-950 px-5 py-20 md:py-28" data-testid="contact-section">
      <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
        <div>
          <SectionHeading eyebrow="Reach out" title="Get in" accent="touch" />
          <a href={telHref(content.contact_phone)} data-testid="contact-phone-link" className="group block rounded-lg border border-red-900/70 bg-red-950/40 p-6 transition-transform duration-300 hover:-translate-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-400">Call {content.contact_name}</p>
            <p className="mt-2 flex items-center gap-3 font-display text-3xl text-gold sm:text-4xl">
              <Phone className="h-7 w-7 transition-transform group-hover:rotate-12" /> {content.contact_phone}
            </p>
            <p className="mt-2 text-sm text-neutral-300">{content.contact_note}</p>
          </a>
          <a href="#vendor" className="mt-4 inline-block text-sm font-semibold text-amber-400 underline-offset-4 hover:underline" data-testid="contact-vendor-link">
            Want vending space? Apply as a vendor →
          </a>
        </div>
        {done ? (
          <SuccessCard testid="contact-success" title="Message received" onReset={() => { setF(EMPTY); setDone(false); }}>
            Thank you for reaching out. The hosts will get back to you soon.
          </SuccessCard>
        ) : (
          <FormCard>
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <Field label="Name" id="c-name" data-testid="contact-name-input" required value={f.name} onChange={set("name")} />
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Email" id="c-email" type="email" data-testid="contact-email-input" required value={f.email} onChange={set("email")} />
                <Field label="Phone (optional)" id="c-phone" type="tel" data-testid="contact-phone-input" value={f.phone} onChange={set("phone")} />
              </div>
              <Field label="Message" id="c-msg">
                <Textarea id="c-msg" required data-testid="contact-message-input" value={f.message} onChange={set("message")} maxLength={3000} className="min-h-[120px] border-neutral-800 bg-neutral-950 text-base" />
              </Field>
              {error && <p className="rounded-md border border-red-800 bg-red-950/50 p-3 text-sm text-red-300" data-testid="contact-error">{error}</p>}
              <SubmitButton loading={loading} testid="contact-submit-button">Send Message</SubmitButton>
            </form>
          </FormCard>
        )}
      </div>
    </section>
  );
};

export const Footer = ({ content }) => (
  <footer className="bg-neutral-950 pb-24 lg:pb-0" data-testid="site-footer">
    <Kente />
    <div className="mx-auto flex max-w-6xl flex-col gap-4 px-5 py-10 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex items-center gap-4">
        <LogoBadge content={content} className="h-16 w-24 rounded-xl p-1" testid="footer-logo" />
        <div>
        <p className="font-display text-3xl font-bold text-gold">
          {content.event_title} <span className="font-script normal-case tracking-normal text-red-500">Festival</span> {content.year}
        </p>
        <p className="mt-1 text-sm text-neutral-400">{content.location_name} · {content.location_address}</p>
        </div>
      </div>
      <div className="max-w-sm space-y-2 sm:text-right">
        <a href="#vendor" className="text-sm font-semibold text-amber-400 hover:underline" data-testid="footer-vendor-link">Vendor applications</a>
        <p className="text-sm italic text-neutral-500">{content.footer_note}</p>
      </div>
    </div>
  </footer>
);
